import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StickyNote, ListChecks, HardHat, Clock } from "lucide-react";
import api from "@/lib/api";
import NotesTab from "./NotesTab";
import ActivitiesTab from "./ActivitiesTab";
import SiteVisitsTab from "./SiteVisitsTab";
import TimelineTab from "./TimelineTab";

export const AUTHOR_COLORS = [
  "border-l-sky-500 bg-sky-50/60", "border-l-emerald-500 bg-emerald-50/60", "border-l-amber-500 bg-amber-50/60",
  "border-l-rose-500 bg-rose-50/60", "border-l-violet-500 bg-violet-50/60", "border-l-teal-500 bg-teal-50/60",
  "border-l-orange-500 bg-orange-50/60", "border-l-indigo-500 bg-indigo-50/60",
];
export function authorTone(id) {
  if (!id) return "border-l-slate-300 bg-slate-50";
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AUTHOR_COLORS[h % AUTHOR_COLORS.length];
}

export default function WorkspaceTabs({ assignment, canManage, onChanged }) {
  const id = assignment.id;
  const [counts, setCounts] = useState({ notes: 0, open: 0, overdue: 0, visits: 0 });
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/assignments/${id}/notes`), api.get(`/assignments/${id}/activities`), api.get(`/assignments/${id}/site-visits`),
    ]).then(([n, a, v]) => {
      if (!alive) return;
      const acts = a.data || [];
      setCounts({ notes: n.data.length, open: acts.filter((x) => ["Da fare", "In corso"].includes(x.status)).length,
        overdue: acts.filter((x) => x.is_overdue).length, visits: v.data.length });
    }).catch(() => {});
    return () => { alive = false; };
  }, [id, tick]);

  const changed = () => { refresh(); onChanged?.(); };
  return (
    <div className="rounded-lg border border-border" data-testid="workspace-panel">
      <div className="border-b border-border px-3 py-2 text-sm font-semibold text-slate-700">Note e attività</div>
      <Tabs defaultValue="note" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-4 rounded-none bg-slate-50 p-1" data-testid="workspace-tabs">
          <TabsTrigger value="note" className="flex-col gap-0.5 py-1.5 text-[11px] sm:flex-row sm:text-xs" data-testid="tab-note">
            <StickyNote className="h-3.5 w-3.5" /> Note {counts.notes ? <span className="rounded-full bg-slate-200 px-1.5 text-[10px]">{counts.notes}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="attivita" className="flex-col gap-0.5 py-1.5 text-[11px] sm:flex-row sm:text-xs" data-testid="tab-attivita">
            <ListChecks className="h-3.5 w-3.5" /> Attività {counts.open ? <span className={`rounded-full px-1.5 text-[10px] ${counts.overdue ? "bg-danger text-white" : "bg-slate-200"}`} data-testid="tab-attivita-count">{counts.open}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="sopralluoghi" className="flex-col gap-0.5 py-1.5 text-[11px] sm:flex-row sm:text-xs" data-testid="tab-sopralluoghi">
            <HardHat className="h-3.5 w-3.5" /> Sopralluoghi {counts.visits ? <span className="rounded-full bg-slate-200 px-1.5 text-[10px]">{counts.visits}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="storico" className="flex-col gap-0.5 py-1.5 text-[11px] sm:flex-row sm:text-xs" data-testid="tab-storico">
            <Clock className="h-3.5 w-3.5" /> Storico
          </TabsTrigger>
        </TabsList>
        <div className="p-3">
          <TabsContent value="note" className="mt-0"><NotesTab assignmentId={id} canManage={canManage} onChanged={changed} /></TabsContent>
          <TabsContent value="attivita" className="mt-0"><ActivitiesTab assignment={assignment} canManage={canManage} onChanged={changed} /></TabsContent>
          <TabsContent value="sopralluoghi" className="mt-0"><SiteVisitsTab assignment={assignment} canManage={canManage} onChanged={changed} /></TabsContent>
          <TabsContent value="storico" className="mt-0"><TimelineTab assignmentId={id} tick={tick} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
